/**
 * BrokersContext
 *
 * Provides shared state management for brokers across desktop and mobile views.
 * Handles data fetching, selection state, dirty tracking, cross-tab sync subscriptions,
 * and URL hydration (reading URL params on initial load to restore selection).
 *
 * Pattern follows CODING_STANDARDS:
 * - Context for truly global state; thin consumers, all logic in provider
 * - Sync refs during render body (not useEffect) for dirty tracking
 * - Queue updates while dirty, apply on dirty→clean transition
 * - URL hydration pattern for desktop (read on mount, write after hydration at page layer)
 * - Auto-select first item when data loads (if none selected)
 * - Service singleton imports (brokerService, syncCoordinatorService)
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { accountService } from '@/services/AccountService';
import { brokerService } from '@/services/BrokerService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { useToastNotification } from '@enfomentis/ui';
import type { Broker, BrokerFeeConfiguration } from '@/types/schema/Broker';
import type { BrokerFeeMetadata } from '@/types/reference/BrokerFeeCatalog';
import type {
  OvernightFinancingStructure,
  CurrencyConversionStructure,
} from '@/types/shared/feeStructures';
import {
  ASSET_CLASS_TYPE_DISPLAY_MAP,
  MARKET_SEGMENT_DISPLAY_MAP,
  SUB_ASSET_CLASS_DISPLAY_MAP,
} from '@/constants/assetClass';
import {
  findConfigByPath,
  buildNodeIdFromConfig,
  createDefaultFeeConfig,
} from '../feeTree/feeConfigUtils';
import type { FeeConfigPath } from '../feeTree/types';

// Re-export for consumers that already import from context
export type { FeeConfigPath } from '../feeTree/types';

// =============================================================================
// Fee Metadata Normalization
// =============================================================================

/**
 * Normalize a broker's optional feeMetadata into a consistent shape with defaults.
 *
 * Guarantees all editable fields are present so form code stays thin.
 * Produces stable key order for reliable JSON.stringify comparison.
 * Preserves deferred fields (supportsMultiCurrency, supportedAccountCurrencies)
 * via conditional spread so they are never dropped during save.
 */
function normalizeFeeMetadata(raw?: BrokerFeeMetadata): BrokerFeeMetadata {
  const currency = raw?.defaultAccountCurrency ?? 'USD';
  const precision = raw?.minimumBalance?.precision ?? 2;

  return {
    defaultAccountCurrency: currency,
    minimumBalance: {
      amount: raw?.minimumBalance?.amount ?? 0,
      precision,
      currency: raw?.minimumBalance?.currency ?? currency,
    },
    platformFees: {
      monthlyFee: raw?.platformFees?.monthlyFee ?? 0,
      dataFees: raw?.platformFees?.dataFees ?? 0,
      inactivityFee: raw?.platformFees?.inactivityFee ?? 0,
      notes: raw?.platformFees?.notes ?? '',
    },
    // Preserve deferred fields if present (conditional spread for exactOptionalPropertyTypes)
    ...(raw?.supportsMultiCurrency !== undefined && {
      supportsMultiCurrency: raw.supportsMultiCurrency,
    }),
    ...(raw?.supportedAccountCurrencies !== undefined && {
      supportedAccountCurrencies: [...raw.supportedAccountCurrencies],
    }),
  };
}

// =============================================================================
// Types
// =============================================================================

/**
 * Parameters for adding a fee configuration
 */
export type AddFeeConfigParams = FeeConfigPath;

/**
 * Fields that callers may update via `updateFeeConfiguration`.
 *
 * Identity fields (id, name, status, instrumentId, category) and the
 * system-stamped `feesUpdatedAt` are excluded — the method manages those.
 */
export type FeeConfigEditableFields = {
  commission: BrokerFeeConfiguration['commission'];
  overnightFinancing: BrokerFeeConfiguration['overnightFinancing'] | undefined;
  currencyConversion: BrokerFeeConfiguration['currencyConversion'] | undefined;
  notes: BrokerFeeConfiguration['notes'];
  appliesTo: BrokerFeeConfiguration['appliesTo'];
  verification: BrokerFeeConfiguration['verification'];
};

/**
 * Parameters for copying a fee configuration
 */
export interface CopyFeeConfigParams {
  /** ID of the source config to copy from */
  sourceConfigId: string;
  /** Destination path for the copy */
  destination: FeeConfigPath;
}

/**
 * Result of addFeeConfiguration operation
 */
export type AddFeeConfigResult =
  | { success: true; nodeId: string }
  | {
      success: false;
      reason: 'no_broker' | 'active_exists' | 'invalid_path' | 'unknown_error';
    };

/**
 * Result of copyFeeConfiguration operation
 */
export type CopyFeeConfigResult =
  | { success: true; nodeId: string }
  | {
      success: false;
      reason:
        | 'no_broker'
        | 'source_not_found'
        | 'active_exists'
        | 'invalid_path'
        | 'unknown_error';
    };

/**
 * Result of overwriteFeeConfiguration operation
 * Same as CopyFeeConfigResult but without 'active_exists' (overwrite replaces active configs)
 */
export type OverwriteFeeConfigResult =
  | { success: true; nodeId: string }
  | {
      success: false;
      reason: 'no_broker' | 'source_not_found' | 'invalid_path' | 'unknown_error';
    };

export interface BrokersContextValue {
  // Data
  brokers: Broker[];
  filteredBrokers: Broker[];
  selectedBroker: Broker | null;
  selectedBrokerId: string | null;
  loading: boolean;
  /** Display-only snapshot of non-archived accounts linked to the selected broker. */
  linkedActiveAccounts: { id: string; name: string }[];

  // Filter state
  showArchived: boolean;

  // Hydration state (for URL sync)
  isHydrating: boolean;

  // Dirty state
  localDescription: string;
  localFeeMetadata: BrokerFeeMetadata;
  localOvernightDefault: OvernightFinancingStructure | undefined;
  localConversionDefault: CurrencyConversionStructure | undefined;
  isDirty: boolean;
  isSaving: boolean;
  showConflictBanner: boolean;

  // Actions
  selectBroker: (id: string | null) => void;
  setShowArchived: (show: boolean) => void;
  reloadBrokers: () => Promise<void>;
  setLocalDescription: (value: string) => void;
  setLocalFeeMetadata: (value: BrokerFeeMetadata) => void;
  setLocalOvernightDefault: (value: OvernightFinancingStructure | undefined) => void;
  setLocalConversionDefault: (value: CurrencyConversionStructure | undefined) => void;
  saveBroker: () => Promise<void>;
  discardBrokerChanges: () => void;
  archiveBroker: () => Promise<boolean>;
  restoreBroker: () => Promise<void>;
  acceptExternalChanges: () => Promise<void>;
  keepEditing: () => void;

  // Fee Configuration Mutations
  addFeeConfiguration: (params: AddFeeConfigParams) => Promise<AddFeeConfigResult>;
  removeFeeConfiguration: (configId: string) => Promise<boolean>;
  copyFeeConfiguration: (params: CopyFeeConfigParams) => Promise<CopyFeeConfigResult>;
  overwriteFeeConfiguration: (params: CopyFeeConfigParams) => Promise<OverwriteFeeConfigResult>;
  /**
   * Update a fee configuration in-place by ID.
   *
   * Uses **shallow merge** (`{ ...existing, ...updates }`). Callers must pass
   * complete nested structures (e.g., the full `commission` object) to avoid
   * wiping sibling fields within nested objects.
   *
   * Only editable fields are accepted — identity fields (id, name, status,
   * instrumentId, category) and `feesUpdatedAt` are excluded at the type level
   * and stripped at runtime as defense-in-depth.
   *
   * @param configId - The ID of the fee configuration to update
   * @param updates - Editable fields to merge into the existing config
   * @returns `true` if the update succeeded, `false` on failure (no broker, config not found)
   */
  updateFeeConfiguration: (
    configId: string,
    updates: Partial<FeeConfigEditableFields>
  ) => Promise<boolean>;
}

// =============================================================================
// Context
// =============================================================================

const BrokersContext = createContext<BrokersContextValue | null>(null);

// =============================================================================
// Provider Props
// =============================================================================

interface BrokersProviderProps {
  children: React.ReactNode;
}

// =============================================================================
// Provider Implementation
// =============================================================================

export const BrokersProvider: React.FC<BrokersProviderProps> = ({ children }) => {
  const { showToast } = useToastNotification();

  // ===========================================================================
  // URL Hydration State (Desktop Only)
  // ===========================================================================

  // Hydration gate: blocks auto-selection effects until URL params are applied
  const [isHydrating, setIsHydrating] = useState(() => {
    if (typeof window === 'undefined') return false;
    const isDesktopInitial = window.matchMedia('(min-width: 768px)').matches;
    if (!isDesktopInitial) return false;
    const params = new URLSearchParams(window.location.search);
    return params.has('brokerId');
  });

  // Capture initial URL params on mount (before any effects can modify URL)
  const initialUrlSelectionRef = useRef<string | null>(null);
  if (initialUrlSelectionRef.current === null && typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    initialUrlSelectionRef.current = params.get('brokerId');
  }

  // ===========================================================================
  // Data State
  // ===========================================================================

  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrokerId, setSelectedBrokerId] = useState<string | null>(null);
  const [showArchived, setShowArchivedState] = useState(false);
  const [linkedActiveAccounts, setLinkedActiveAccounts] = useState<
    { id: string; name: string }[]
  >([]);

  // ===========================================================================
  // Dirty State
  // ===========================================================================

  const [localDescription, setLocalDescriptionState] = useState('');
  const lastSavedDescriptionRef = useRef('');
  const [localFeeMetadata, setLocalFeeMetadataState] = useState<BrokerFeeMetadata>(
    () => normalizeFeeMetadata()
  );
  const lastSavedMetadataRef = useRef<string>(
    JSON.stringify(normalizeFeeMetadata())
  );
  const [localOvernightDefault, setLocalOvernightDefaultState] = useState<
    OvernightFinancingStructure | undefined
  >(undefined);
  const lastSavedOvernightRef = useRef<string>('null');
  const [localConversionDefault, setLocalConversionDefaultState] = useState<
    CurrencyConversionStructure | undefined
  >(undefined);
  const lastSavedConversionRef = useRef<string>('null');
  const [isSaving, setIsSaving] = useState(false);

  // Sync conflict state
  const [hasQueuedUpdate, setHasQueuedUpdate] = useState(false);
  const [dismissedExternalUpdate, setDismissedExternalUpdate] = useState(false);

  // Computed dirty state — stringify metadata/defaults once per render for comparison
  const metadataJson = JSON.stringify(localFeeMetadata);
  const overnightJson = JSON.stringify(localOvernightDefault ?? null);
  const conversionJson = JSON.stringify(localConversionDefault ?? null);
  const isDirty =
    localDescription !== lastSavedDescriptionRef.current ||
    metadataJson !== lastSavedMetadataRef.current ||
    overnightJson !== lastSavedOvernightRef.current ||
    conversionJson !== lastSavedConversionRef.current;
  const showConflictBanner = hasQueuedUpdate && isDirty && !dismissedExternalUpdate;

  // ===========================================================================
  // Refs for Sync Callbacks (sync during render body, not useEffect)
  // ===========================================================================

  const isDirtyRef = useRef(isDirty);
  const selectedBrokerIdRef = useRef(selectedBrokerId);

  // Track previous synced ID to detect ID change vs same-ID update
  const prevSyncedBrokerIdRef = useRef<string | null>(null);

  // Sync refs during render body per CODING_STANDARDS
  isDirtyRef.current = isDirty;
  selectedBrokerIdRef.current = selectedBrokerId;

  // ===========================================================================
  // Data Loading Functions
  // ===========================================================================

  const loadBrokers = useCallback(async () => {
    try {
      setLoading(true);
      const allBrokers = await brokerService.getAll();
      setBrokers(allBrokers);
    } catch (error) {
      console.error('Failed to load brokers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // ===========================================================================
  // Initial Data Load
  // ===========================================================================

  useEffect(() => {
    loadBrokers();
  }, [loadBrokers]);

  // ===========================================================================
  // Sync Subscription (Cross-Tab)
  // ===========================================================================

  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('brokers', () => {
      if (isDirtyRef.current) {
        // Queue update, don't apply immediately
        setHasQueuedUpdate(true);
        setDismissedExternalUpdate(false);
      } else {
        // Not dirty, reload immediately
        loadBrokers();
      }
    });

    return () => unsubscribe();
  }, [loadBrokers]);

  // ===========================================================================
  // URL Hydration Effect (Desktop Only)
  // ===========================================================================

  useEffect(() => {
    // Only run while hydrating
    if (!isHydrating) return;

    // Wait for data to load
    if (loading) return;

    const urlBrokerId = initialUrlSelectionRef.current;

    // If no URL param, complete hydration immediately
    if (!urlBrokerId) {
      setIsHydrating(false);
      return;
    }

    // Apply broker selection from URL
    const broker = brokers.find((b) => b.id === urlBrokerId);
    if (broker) {
      // Enable showArchived if broker is archived
      if (broker.status === 'archived' && !showArchived) {
        setShowArchivedState(true);
      }
      // Select the broker if not already selected
      if (selectedBrokerId !== urlBrokerId) {
        setSelectedBrokerId(urlBrokerId);
      }
    }
    // Invalid broker ID or selection complete - finish hydration
    setIsHydrating(false);
  }, [isHydrating, loading, brokers, selectedBrokerId, showArchived]);

  // ===========================================================================
  // Derived State (must be before auto-selection effect)
  // ===========================================================================

  const selectedBroker = useMemo(() => {
    if (!selectedBrokerId) return null;
    return brokers.find((b) => b.id === selectedBrokerId) ?? null;
  }, [brokers, selectedBrokerId]);

  // When showArchived=false, exclude only archived (keep active + inactive visible)
  const filteredBrokers = useMemo(() => {
    return showArchived
      ? brokers
      : brokers.filter((b) => b.status !== 'archived');
  }, [brokers, showArchived]);

  // ===========================================================================
  // Auto-Selection Effect
  // ===========================================================================

  useEffect(() => {
    // Gate: skip during URL hydration to avoid race conditions
    if (isHydrating) return;

    // Auto-select first visible broker when data loads (if none selected)
    // Use filteredBrokers to respect showArchived setting
    const firstBroker = filteredBrokers[0];
    if (!loading && firstBroker && !selectedBrokerId) {
      setSelectedBrokerId(firstBroker.id);
    }
  }, [loading, filteredBrokers, selectedBrokerId, isHydrating]);

  // ===========================================================================
  // Linked Accounts Snapshot (display-only)
  // ===========================================================================

  useEffect(() => {
    if (!selectedBrokerId) {
      setLinkedActiveAccounts([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const accounts = await accountService.getByBrokerId(selectedBrokerId);
        if (cancelled) return;
        const active = accounts
          .filter((a) => a.status !== 'archived')
          .map((a) => ({ id: a.id, name: a.name }));
        setLinkedActiveAccounts(active);
      } catch (error) {
        console.error('Failed to load linked accounts:', error);
        if (!cancelled) setLinkedActiveAccounts([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedBrokerId]);

  // ===========================================================================
  // Sync Local State with Selected Broker
  // ===========================================================================

  useEffect(() => {
    // Gate: skip during URL hydration to avoid overwriting URL-selected broker
    if (isHydrating) return;

    const isIdChange = selectedBroker?.id !== prevSyncedBrokerIdRef.current;
    prevSyncedBrokerIdRef.current = selectedBroker?.id ?? null;

    if (selectedBroker) {
      // Always sync on ID change (selection change)
      // Only sync on updatedAt change if not dirty (cross-tab update)
      if (isIdChange || !isDirtyRef.current) {
        const description = selectedBroker.description ?? '';
        setLocalDescriptionState(description);
        lastSavedDescriptionRef.current = description;

        const metadata = normalizeFeeMetadata(selectedBroker.feeMetadata);
        setLocalFeeMetadataState(metadata);
        lastSavedMetadataRef.current = JSON.stringify(metadata);

        const overnightDefault = selectedBroker.brokerOvernightFinancingDefault
          ? structuredClone(selectedBroker.brokerOvernightFinancingDefault)
          : undefined;
        setLocalOvernightDefaultState(overnightDefault);
        lastSavedOvernightRef.current = JSON.stringify(overnightDefault ?? null);

        const conversionDefault = selectedBroker.brokerCurrencyConversionDefault
          ? structuredClone(selectedBroker.brokerCurrencyConversionDefault)
          : undefined;
        setLocalConversionDefaultState(conversionDefault);
        lastSavedConversionRef.current = JSON.stringify(conversionDefault ?? null);
      }
      // Reset conflict state only on ID change
      if (isIdChange) {
        setHasQueuedUpdate(false);
        setDismissedExternalUpdate(false);
      }
    } else {
      setLocalDescriptionState('');
      lastSavedDescriptionRef.current = '';

      const defaults = normalizeFeeMetadata();
      setLocalFeeMetadataState(defaults);
      lastSavedMetadataRef.current = JSON.stringify(defaults);

      setLocalOvernightDefaultState(undefined);
      lastSavedOvernightRef.current = 'null';

      setLocalConversionDefaultState(undefined);
      lastSavedConversionRef.current = 'null';
    }
  }, [selectedBroker, isHydrating]);

  // ===========================================================================
  // Actions
  // ===========================================================================

  const selectBroker = useCallback((id: string | null) => {
    setSelectedBrokerId(id);
  }, []);

  const setShowArchived = useCallback(
    (show: boolean) => {
      if (!show && selectedBroker?.status === 'archived') {
        // Turning off while archived broker selected - auto-select first non-archived
        const firstNonArchived = brokers.find((b) => b.status !== 'archived');
        if (firstNonArchived) {
          setSelectedBrokerId(firstNonArchived.id);
        } else {
          setSelectedBrokerId(null);
        }
      }
      setShowArchivedState(show);
    },
    [selectedBroker, brokers]
  );

  const reloadBrokers = useCallback(async () => {
    await loadBrokers();
  }, [loadBrokers]);

  const setLocalDescription = useCallback((value: string) => {
    setLocalDescriptionState(value);
  }, []);

  const setLocalFeeMetadata = useCallback((value: BrokerFeeMetadata) => {
    setLocalFeeMetadataState(value);
  }, []);

  const saveBroker = useCallback(async () => {
    if (!selectedBroker || !isDirty) return;

    setIsSaving(true);
    try {
      // Build explicit updates object so we can handle removal of optional
      // broker-level defaults (baseline was set → now undefined → clear from DB).
      const updates: Record<string, unknown> = {
        description: localDescription,
        feeMetadata: localFeeMetadata,
      };

      // Overnight financing default: include if set, clear if removed, omit if never set
      if (localOvernightDefault !== undefined) {
        updates['brokerOvernightFinancingDefault'] = localOvernightDefault;
      } else if (lastSavedOvernightRef.current !== 'null') {
        updates['brokerOvernightFinancingDefault'] = undefined;
      }

      // Currency conversion default: same pattern
      if (localConversionDefault !== undefined) {
        updates['brokerCurrencyConversionDefault'] = localConversionDefault;
      } else if (lastSavedConversionRef.current !== 'null') {
        updates['brokerCurrencyConversionDefault'] = undefined;
      }

      await brokerService.update(
        selectedBroker.id,
        updates as Parameters<typeof brokerService.update>[1],
      );
      syncCoordinatorService.notifyChange('brokers', selectedBroker.id);
      lastSavedDescriptionRef.current = localDescription;
      lastSavedMetadataRef.current = JSON.stringify(localFeeMetadata);
      lastSavedOvernightRef.current = JSON.stringify(localOvernightDefault ?? null);
      lastSavedConversionRef.current = JSON.stringify(localConversionDefault ?? null);
      setHasQueuedUpdate(false);
      showToast({
        title: 'Broker Saved',
        description: `"${selectedBroker.name}" updated`,
        color: 'success',
      });
      await loadBrokers();
    } catch (error) {
      console.error('Failed to save broker:', error);
      showToast({
        title: 'Save Failed',
        description: 'Could not save broker. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedBroker, isDirty, localDescription, localFeeMetadata, localOvernightDefault, localConversionDefault, showToast, loadBrokers]);

  const discardBrokerChanges = useCallback(() => {
    setLocalDescriptionState(lastSavedDescriptionRef.current);
    // Defensive parse with fallback to defaults
    try {
      setLocalFeeMetadataState(
        JSON.parse(lastSavedMetadataRef.current) as BrokerFeeMetadata
      );
    } catch {
      const fallback = normalizeFeeMetadata();
      setLocalFeeMetadataState(fallback);
      lastSavedMetadataRef.current = JSON.stringify(fallback);
    }
    // Restore financing/conversion defaults from baseline
    try {
      const parsedOvernight = JSON.parse(lastSavedOvernightRef.current) as
        | OvernightFinancingStructure
        | null;
      setLocalOvernightDefaultState(parsedOvernight ?? undefined);
    } catch {
      setLocalOvernightDefaultState(undefined);
      lastSavedOvernightRef.current = 'null';
    }
    try {
      const parsedConversion = JSON.parse(lastSavedConversionRef.current) as
        | CurrencyConversionStructure
        | null;
      setLocalConversionDefaultState(parsedConversion ?? undefined);
    } catch {
      setLocalConversionDefaultState(undefined);
      lastSavedConversionRef.current = 'null';
    }
    // If there's a queued external update, apply it now
    if (hasQueuedUpdate) {
      setHasQueuedUpdate(false);
      setDismissedExternalUpdate(false);
      loadBrokers();
    }
  }, [hasQueuedUpdate, loadBrokers]);

  const archiveBroker = useCallback(async (): Promise<boolean> => {
    if (!selectedBroker) return false;

    // Guard: block archive if non-archived accounts still reference this broker
    let activeLinked: Awaited<ReturnType<typeof accountService.getByBrokerId>> = [];
    try {
      const linkedAccounts = await accountService.getByBrokerId(selectedBroker.id);
      activeLinked = linkedAccounts.filter(a => a.status !== 'archived');
    } catch (error) {
      console.error('Failed to check linked accounts:', error);
      showToast({
        title: 'Archive Failed',
        description: 'Could not check linked accounts. Please try again.',
        color: 'danger',
      });
      return false;
    }
    if (activeLinked.length > 0) {
      const names = activeLinked.slice(0, 3).map(a => a.name).join(', ');
      const suffix = activeLinked.length > 3 ? `, and ${activeLinked.length - 3} more` : '';
      showToast({
        title: 'Cannot Archive Broker',
        description: `This broker is linked to ${activeLinked.length} account(s): ${names}${suffix}. Reassign or archive those accounts first.`,
        color: 'warning',
      });
      return false;
    }

    setIsSaving(true);
    try {
      await brokerService.update(selectedBroker.id, { status: 'archived' });
      syncCoordinatorService.notifyChange('brokers', selectedBroker.id);
      // Auto-toggle showArchived on to keep selection visible
      setShowArchivedState(true);
      showToast({
        title: 'Broker Archived',
        description: `"${selectedBroker.name}" has been archived`,
        color: 'success',
      });
      await loadBrokers();
      return true;
    } catch (error) {
      console.error('Failed to archive broker:', error);
      showToast({
        title: 'Archive Failed',
        description: 'Could not archive broker. Please try again.',
        color: 'danger',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [selectedBroker, showToast, loadBrokers]);

  const restoreBroker = useCallback(async () => {
    if (!selectedBroker) return;

    setIsSaving(true);
    try {
      await brokerService.update(selectedBroker.id, { status: 'active' });
      syncCoordinatorService.notifyChange('brokers', selectedBroker.id);
      showToast({
        title: 'Broker Restored',
        description: `"${selectedBroker.name}" is now active`,
        color: 'success',
      });
      await loadBrokers();
    } catch (error) {
      console.error('Failed to restore broker:', error);
      showToast({
        title: 'Restore Failed',
        description: 'Could not restore broker. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedBroker, showToast, loadBrokers]);

  const acceptExternalChanges = useCallback(async () => {
    if (!selectedBrokerId) return;

    // Reload brokers list
    await loadBrokers();

    // Fetch fresh broker and apply fields directly
    const freshBroker = await brokerService.get(selectedBrokerId);
    if (freshBroker) {
      const description = freshBroker.description ?? '';
      setLocalDescriptionState(description);
      lastSavedDescriptionRef.current = description;

      const metadata = normalizeFeeMetadata(freshBroker.feeMetadata);
      setLocalFeeMetadataState(metadata);
      lastSavedMetadataRef.current = JSON.stringify(metadata);

      const overnightDefault = freshBroker.brokerOvernightFinancingDefault
        ? structuredClone(freshBroker.brokerOvernightFinancingDefault)
        : undefined;
      setLocalOvernightDefaultState(overnightDefault);
      lastSavedOvernightRef.current = JSON.stringify(overnightDefault ?? null);

      const conversionDefault = freshBroker.brokerCurrencyConversionDefault
        ? structuredClone(freshBroker.brokerCurrencyConversionDefault)
        : undefined;
      setLocalConversionDefaultState(conversionDefault);
      lastSavedConversionRef.current = JSON.stringify(conversionDefault ?? null);
    } else {
      // Broker was deleted - notify user and clear selection
      showToast({
        title: 'Broker No Longer Exists',
        description: 'This broker was deleted in another tab.',
        color: 'warning',
      });
      setSelectedBrokerId(null);
      setLocalDescriptionState('');
      lastSavedDescriptionRef.current = '';

      const defaults = normalizeFeeMetadata();
      setLocalFeeMetadataState(defaults);
      lastSavedMetadataRef.current = JSON.stringify(defaults);

      setLocalOvernightDefaultState(undefined);
      lastSavedOvernightRef.current = 'null';

      setLocalConversionDefaultState(undefined);
      lastSavedConversionRef.current = 'null';
    }

    // Clear conflict state
    setHasQueuedUpdate(false);
    setDismissedExternalUpdate(false);
  }, [loadBrokers, selectedBrokerId, showToast]);

  const keepEditing = useCallback(() => {
    setDismissedExternalUpdate(true);
  }, []);

  // ===========================================================================
  // Fee Configuration Mutations
  // ===========================================================================

  /** Display maps for building config names (memoized to avoid recreation) */
  const displayMaps = useMemo(
    () => ({
      assetClassType: ASSET_CLASS_TYPE_DISPLAY_MAP,
      marketSegment: MARKET_SEGMENT_DISPLAY_MAP,
      subAssetClass: SUB_ASSET_CLASS_DISPLAY_MAP,
    }),
    []
  );

  const addFeeConfiguration = useCallback(
    async (params: AddFeeConfigParams): Promise<AddFeeConfigResult> => {
      const { category, instrumentId } = params;

      // Guard: invalid path - must have at least category or instrumentId
      if (!category && !instrumentId) {
        return { success: false, reason: 'invalid_path' };
      }

      // Guard: no broker selected
      if (!selectedBrokerId) {
        return { success: false, reason: 'no_broker' };
      }

      try {
        // Fetch fresh broker state to avoid lost updates
        const broker = await brokerService.get(selectedBrokerId);
        if (!broker) {
          return { success: false, reason: 'no_broker' };
        }

        // Check for existing config at this path
        const existingConfig = findConfigByPath(
          broker.feeConfigurations,
          category,
          instrumentId
        );

        let updatedConfigs: BrokerFeeConfiguration[];
        let newConfig: BrokerFeeConfiguration;

        if (existingConfig) {
          if (existingConfig.status !== 'archived') {
            // Active config exists - UI should handle duplicate warning
            return { success: false, reason: 'active_exists' };
          }

          // Archived config exists - reactivate it
          newConfig = {
            ...existingConfig,
            status: 'active',
            feesUpdatedAt: new Date().toISOString(),
          };
          updatedConfigs = broker.feeConfigurations.map((c) =>
            c.id === existingConfig.id ? newConfig : c
          );
        } else {
          // No existing config - create new
          // Use conditional spread to satisfy exactOptionalPropertyTypes
          newConfig = createDefaultFeeConfig({
            ...(category && { category }),
            ...(instrumentId && { instrumentId }),
            displayMaps,
          });
          updatedConfigs = [...broker.feeConfigurations, newConfig];
        }

        // Update broker with new configs
        await brokerService.update(selectedBrokerId, {
          feeConfigurations: updatedConfigs,
        });
        syncCoordinatorService.notifyChange('brokers', selectedBrokerId);
        await loadBrokers();

        const nodeId = buildNodeIdFromConfig(newConfig);
        return { success: true, nodeId };
      } catch (error) {
        console.error('Failed to add fee configuration:', error);
        return { success: false, reason: 'unknown_error' };
      }
    },
    [selectedBrokerId, displayMaps, loadBrokers]
  );

  /**
   * Remove (archive) a fee configuration by ID.
   *
   * @param configId - The ID of the fee configuration to archive
   * @returns `true` if the config was archived (state changed), `false` if no change was made
   *          (config not found, already archived, or no broker selected). This is NOT idempotent -
   *          callers that need "desired state achieved" semantics should check config status separately.
   */
  const removeFeeConfiguration = useCallback(
    async (configId: string): Promise<boolean> => {
      // Guard: no broker selected
      if (!selectedBrokerId) {
        return false;
      }

      try {
        // Fetch fresh broker state
        const broker = await brokerService.get(selectedBrokerId);
        if (!broker) {
          return false;
        }

        // Find config by ID
        const config = broker.feeConfigurations.find((c) => c.id === configId);
        if (!config) {
          return false;
        }

        // If already archived, no-op
        if (config.status === 'archived') {
          return false;
        }

        // Archive the config (soft delete)
        const updatedConfigs = broker.feeConfigurations.map((c) =>
          c.id === configId ? { ...c, status: 'archived' as const } : c
        );

        await brokerService.update(selectedBrokerId, {
          feeConfigurations: updatedConfigs,
        });
        syncCoordinatorService.notifyChange('brokers', selectedBrokerId);
        await loadBrokers();

        return true;
      } catch (error) {
        console.error('Failed to remove fee configuration:', error);
        return false;
      }
    },
    [selectedBrokerId, loadBrokers]
  );

  /**
   * Update a fee configuration in-place by ID.
   *
   * Uses shallow merge — callers must pass complete nested structures
   * (e.g., the full `commission` object) to avoid wiping sibling fields.
   * Immutable fields are stripped at runtime as defense-in-depth.
   */
  const updateFeeConfiguration = useCallback(
    async (
      configId: string,
      updates: Partial<FeeConfigEditableFields>
    ): Promise<boolean> => {
      // Guard: no broker selected
      if (!selectedBrokerId) {
        return false;
      }

      try {
        // Fetch fresh broker state to avoid lost updates
        const broker = await brokerService.get(selectedBrokerId);
        if (!broker) {
          return false;
        }

        // Find config by ID
        const existingConfig = broker.feeConfigurations.find(
          (c) => c.id === configId
        );
        if (!existingConfig) {
          return false;
        }

        // Runtime strip of immutable fields (defense-in-depth)
        const safeUpdates = { ...updates };
        const IMMUTABLE_KEYS = ['id', 'name', 'status', 'instrumentId', 'category', 'feesUpdatedAt'] as const;
        for (const key of IMMUTABLE_KEYS) {
          delete (safeUpdates as Record<string, unknown>)[key];
        }

        // Shallow merge safe updates + stamp feesUpdatedAt
        // Type assertion safe: existingConfig provides all required fields,
        // safeUpdates only overrides with caller-provided editable values
        const updatedConfig = {
          ...existingConfig,
          ...safeUpdates,
          feesUpdatedAt: new Date().toISOString(),
        } as BrokerFeeConfiguration;

        // Build updated array
        const updatedConfigs = broker.feeConfigurations.map((c) =>
          c.id === configId ? updatedConfig : c
        );

        await brokerService.update(selectedBrokerId, {
          feeConfigurations: updatedConfigs,
        });
        syncCoordinatorService.notifyChange('brokers', selectedBrokerId);
        await loadBrokers();

        return true;
      } catch (error) {
        console.error('Failed to update fee configuration:', error);
        return false;
      }
    },
    [selectedBrokerId, loadBrokers]
  );

  // Shared copy logic — used by both copyFeeConfiguration and overwriteFeeConfiguration
  const performCopy = useCallback(async (
    params: CopyFeeConfigParams,
    allowOverwrite: boolean
  ): Promise<CopyFeeConfigResult> => {
    const { sourceConfigId, destination } = params;

    // Guard: invalid destination path - must have at least category or instrumentId
    if (!destination.category && !destination.instrumentId) {
      return { success: false, reason: 'invalid_path' };
    }

    // Guard: no broker selected
    if (!selectedBrokerId) {
      return { success: false, reason: 'no_broker' };
    }

    try {
      // Fetch fresh broker state
      const broker = await brokerService.get(selectedBrokerId);
      if (!broker) {
        return { success: false, reason: 'no_broker' };
      }

      // Find source config
      const sourceConfig = broker.feeConfigurations.find(
        (c) => c.id === sourceConfigId
      );
      if (!sourceConfig) {
        return { success: false, reason: 'source_not_found' };
      }

      // Check for existing config at destination
      const existingConfig = findConfigByPath(
        broker.feeConfigurations,
        destination.category,
        destination.instrumentId
      );

      let updatedConfigs: BrokerFeeConfiguration[];
      let destConfig: BrokerFeeConfiguration;

      if (existingConfig) {
        if (existingConfig.status !== 'archived' && !allowOverwrite) {
          // Active config at destination - UI should handle overwrite warning
          return { success: false, reason: 'active_exists' };
        }

        // Overwrite active config or reactivate archived config with source fee data
        // Build from identity/path fields only; all fee data from source
        // This ensures no stale fields from the existing config leak through
        destConfig = {
          id: existingConfig.id,
          name: existingConfig.name,
          status: 'active' as const,
          feesUpdatedAt: new Date().toISOString(),
          // Path fields from destination
          ...(existingConfig.category && { category: existingConfig.category }),
          ...(existingConfig.instrumentId && {
            instrumentId: existingConfig.instrumentId,
          }),
          // Fee data exclusively from source
          commission: { ...sourceConfig.commission },
          ...(sourceConfig.overnightFinancing && {
            overnightFinancing: { ...sourceConfig.overnightFinancing },
          }),
          ...(sourceConfig.currencyConversion && {
            currencyConversion: { ...sourceConfig.currencyConversion },
          }),
          ...(sourceConfig.notes !== undefined && {
            notes: sourceConfig.notes,
          }),
        };
        updatedConfigs = broker.feeConfigurations.map((c) =>
          c.id === existingConfig.id ? destConfig : c
        );
      } else {
        // No config at destination - create new with source fee data
        const baseName =
          destination.instrumentId ??
          (destination.category
            ? [
                displayMaps.assetClassType[destination.category.assetClass],
                displayMaps.marketSegment[destination.category.marketSegment],
                destination.category.subAssetClass
                  ? displayMaps.subAssetClass[destination.category.subAssetClass]
                  : null,
              ]
                .filter(Boolean)
                .join(' / ')
            : 'Unnamed Configuration');

        // Use conditional spread to satisfy exactOptionalPropertyTypes
        destConfig = createDefaultFeeConfig({
          ...(destination.category && { category: destination.category }),
          ...(destination.instrumentId && { instrumentId: destination.instrumentId }),
          displayMaps,
        });
        // Overwrite name if we calculated one
        destConfig.name = baseName;
        // Copy fee data from source
        destConfig.commission = { ...sourceConfig.commission };
        if (sourceConfig.overnightFinancing) {
          destConfig.overnightFinancing = { ...sourceConfig.overnightFinancing };
        }
        if (sourceConfig.currencyConversion) {
          destConfig.currencyConversion = { ...sourceConfig.currencyConversion };
        }
        if (sourceConfig.notes !== undefined) {
          destConfig.notes = sourceConfig.notes;
        }
        // Note: verification intentionally not copied - cloned data isn't verified
        // createDefaultFeeConfig creates config without verification

        updatedConfigs = [...broker.feeConfigurations, destConfig];
      }

      // Update broker with new configs
      await brokerService.update(selectedBrokerId, {
        feeConfigurations: updatedConfigs,
      });
      syncCoordinatorService.notifyChange('brokers', selectedBrokerId);
      await loadBrokers();

      const nodeId = buildNodeIdFromConfig(destConfig);
      return { success: true, nodeId };
    } catch (error) {
      console.error('Failed to copy fee configuration:', error);
      return { success: false, reason: 'unknown_error' };
    }
  }, [selectedBrokerId, displayMaps, loadBrokers]);

  const copyFeeConfiguration = useCallback(
    async (params: CopyFeeConfigParams): Promise<CopyFeeConfigResult> => {
      return performCopy(params, false);
    },
    [performCopy]
  );

  const overwriteFeeConfiguration = useCallback(
    async (params: CopyFeeConfigParams): Promise<OverwriteFeeConfigResult> => {
      const result = await performCopy(params, true);
      if (result.success) return result;
      const { reason } = result;
      if (reason === 'active_exists') {
        // Structurally unreachable when allowOverwrite=true — indicates a logic error
        throw new Error('Internal error: active_exists returned with allowOverwrite=true');
      }
      return { success: false, reason };
    },
    [performCopy]
  );

  // ===========================================================================
  // Context Value (Memoized)
  // ===========================================================================

  const value = useMemo<BrokersContextValue>(
    () => ({
      // Data
      brokers,
      filteredBrokers,
      selectedBroker,
      selectedBrokerId,
      loading,
      linkedActiveAccounts,
      showArchived,
      isHydrating,

      // Dirty state
      localDescription,
      localFeeMetadata,
      localOvernightDefault,
      localConversionDefault,
      isDirty,
      isSaving,
      showConflictBanner,

      // Actions
      selectBroker,
      setShowArchived,
      reloadBrokers,
      setLocalDescription,
      setLocalFeeMetadata,
      setLocalOvernightDefault: setLocalOvernightDefaultState,
      setLocalConversionDefault: setLocalConversionDefaultState,
      saveBroker,
      discardBrokerChanges,
      archiveBroker,
      restoreBroker,
      acceptExternalChanges,
      keepEditing,

      // Fee Configuration Mutations
      addFeeConfiguration,
      removeFeeConfiguration,
      copyFeeConfiguration,
      overwriteFeeConfiguration,
      updateFeeConfiguration,
    }),
    [
      // Data
      brokers,
      filteredBrokers,
      selectedBroker,
      selectedBrokerId,
      loading,
      linkedActiveAccounts,
      showArchived,
      isHydrating,

      // Dirty state
      localDescription,
      localFeeMetadata,
      localOvernightDefault,
      localConversionDefault,
      isDirty,
      isSaving,
      showConflictBanner,

      // Actions
      selectBroker,
      setShowArchived,
      reloadBrokers,
      setLocalDescription,
      setLocalFeeMetadata,
      setLocalOvernightDefaultState,
      setLocalConversionDefaultState,
      saveBroker,
      discardBrokerChanges,
      archiveBroker,
      restoreBroker,
      acceptExternalChanges,
      keepEditing,

      // Fee Configuration Mutations
      addFeeConfiguration,
      removeFeeConfiguration,
      copyFeeConfiguration,
      overwriteFeeConfiguration,
      updateFeeConfiguration,
    ]
  );

  return (
    <BrokersContext.Provider value={value}>
      {children}
    </BrokersContext.Provider>
  );
};

// =============================================================================
// Consumer Hook
// =============================================================================

/**
 * Hook to consume the BrokersContext.
 * Must be used within a BrokersProvider.
 *
 * @throws Error if used outside of BrokersProvider
 */
export function useBrokers(): BrokersContextValue {
  const context = useContext(BrokersContext);
  if (!context) {
    throw new Error(
      'useBrokers must be used within a BrokersProvider. ' +
        'Wrap your component tree with <BrokersProvider>.'
    );
  }
  return context;
}
